import { Stack, Text } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  type ColumnDefinition,
  DataTable,
  Link,
  Pagination,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import type { ReactNode } from "react";
import type { SortDescriptor } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { formatMonitoringDate } from "../monitoringFormat";
import {
  formatLifecycleStatusText,
  isLifecycleActivityLogRow,
} from "../monitoringLifecycleText";
import { getActivityLogChangeAttributeItems } from "../monitoringRunChangeText";
import type { EnrollmentDetailCheck, MonitoringActivityLogRun } from "../types";
import {
  ActivityLogFilters,
  type ActivityLogFilterState,
} from "./ActivityLogFilters";
import { monitoringTabCardContainerStyle } from "./monitoringStyles";
import {
  isLastRunStatusValue,
  isMonitorStatusLabelKey,
  isRunResultValue,
  useLastRunStatusLabels,
  useMonitorStatusLabels,
  useRunResultLabels,
} from "./monitoringTags";

export type { ActivityLogFilterState };

export interface ActivityLogTabProps {
  rows: MonitoringActivityLogRun[];
  count: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  filters: ActivityLogFilterState;
  onFiltersChange: (next: ActivityLogFilterState) => void;
  onResetPage: () => void;
  checkOptions: EnrollmentDetailCheck[];
  sortDescriptor: SortDescriptor;
  onSortChange: (next: SortDescriptor) => void;
  description?: string;
  onExport?: () => void;
  isExporting?: boolean;
  renderCaseLink?: (shortId: string) => ReactNode;
}

function DefaultCaseLink({ shortId }: { shortId: string }) {
  return (
    <Link href={`/case-details/${shortId}`} testID="activity-log-case-link">
      {shortId}
    </Link>
  );
}

export function ActivityLogTab({
  rows,
  count,
  isLoading,
  page,
  pageSize,
  onPageChange,
  filters,
  onFiltersChange,
  onResetPage,
  checkOptions,
  sortDescriptor,
  onSortChange,
  description,
  onExport,
  isExporting = false,
  renderCaseLink,
}: ActivityLogTabProps) {
  const { t } = useTranslation();
  const runStatusLabels = useLastRunStatusLabels();
  const monitorStatusLabels = useMonitorStatusLabels();
  const runResultLabels = useRunResultLabels();
  const renderLink =
    renderCaseLink ?? ((shortId) => <DefaultCaseLink shortId={shortId} />);

  const columns: ColumnDefinition<MonitoringActivityLogRun>[] = [
    {
      key: "run_date",
      name: t("Run date"),
      width: 140,
      isRowHeader: true,
      allowsSorting: true,
      render: (run) => (
        <Text style="form/body-secondary">
          {formatMonitoringDate(run.run_date) || t("—")}
        </Text>
      ),
    },
    {
      key: "check_label",
      name: t("Check"),
      width: 200,
      render: (run) => (
        <Text style="form/body-secondary">{run.check_label}</Text>
      ),
    },
    {
      key: "monitor_status",
      name: t("Status"),
      width: 180,
      render: (run) => (
        <Text style="form/body-secondary">
          {isLifecycleActivityLogRow(run)
            ? formatLifecycleStatusText(run, monitorStatusLabels)
            : isLastRunStatusValue(run.monitor_status)
              ? runStatusLabels[run.monitor_status]
              : isMonitorStatusLabelKey(run.monitor_status)
                ? monitorStatusLabels[run.monitor_status]
                : run.monitor_status}
        </Text>
      ),
    },
    {
      key: "result_status",
      name: t("Run result"),
      width: 160,
      render: (run) => (
        <Text style="form/body-secondary">
          {run.result_status
            ? isRunResultValue(run.result_status)
              ? runResultLabels[run.result_status]
              : run.result_status
            : t("—")}
        </Text>
      ),
    },
    {
      key: "changes",
      name: t("Change attribute"),
      width: 280,
      render: (run) => {
        const items = getActivityLogChangeAttributeItems(run, t);
        if (items.length === 1) {
          return <Text style="form/body-secondary">{items[0]}</Text>;
        }
        return (
          <ul className={styles.changeAttributeList}>
            {items.map((item, index) => (
              <li key={index} className={styles.changeAttributeListItem}>
                <Text style="form/body-secondary">{item}</Text>
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      key: "monitor_case_short_id",
      name: t("Monitor ID"),
      width: 140,
      render: (run) =>
        run.monitor_case_short_id ? (
          renderLink(run.monitor_case_short_id)
        ) : (
          <Text style="form/body-secondary">{t("—")}</Text>
        ),
    },
    {
      key: "base_case_short_id",
      name: t("Base case ID"),
      width: 140,
      render: (run) =>
        !isLifecycleActivityLogRow(run) && run.base_case_short_id ? (
          renderLink(run.base_case_short_id)
        ) : (
          <Text style="form/body-secondary">{t("—")}</Text>
        ),
    },
  ];

  return (
    <Card
      className={monitoringTabCardContainerStyle}
      type="large"
      responsive
      testID="monitoring-activity-log-card"
    >
      <Stack gap="space-4" testID="monitoring-activity-log">
        <div className={styles.titleRow}>
          <Stack gap="space-2">
            <Text
              testID="monitoring-activity-log-title"
              style="global/heading-large"
            >
              {t("Activity log")}
            </Text>
            {description ? (
              <Text style="form/body-secondary" color="text-weak">
                {description}
              </Text>
            ) : null}
          </Stack>
          {onExport ? (
            <BasicButton
              onPress={onExport}
              isDisabled={isExporting || count === 0}
              testID="activity-log-export-button"
            >
              {t("Download CSV")}
            </BasicButton>
          ) : null}
        </div>

        <ActivityLogFilters
          filters={filters}
          checkOptions={checkOptions}
          onChange={onFiltersChange}
          onResetPage={onResetPage}
        />

        <DataTable
          columns={columns}
          rows={rows}
          rowKey="id"
          isLoading={isLoading}
          aria-label={t("Monitoring activity log")}
          testId="activity-log-table"
          minWidth="100%"
          disableScrolling="visible"
          sortDescriptor={sortDescriptor}
          onSortChange={(next) => {
            onSortChange(next);
            onResetPage();
          }}
          renderEmptyState={() => (
            <Text style="form/body-secondary">
              {t("No monitoring activity yet.")}
            </Text>
          )}
        />

        <Pagination
          setPage={onPageChange}
          total={count}
          page={page}
          pageCount={pageSize}
        />
      </Stack>
    </Card>
  );
}

const styles = {
  titleRow: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "var(--space-3)",
    flexWrap: "wrap",
  }),
  changeAttributeList: css({
    margin: 0,
    padding: 0,
    listStyle: "none",
  }),
  changeAttributeListItem: css({
    position: "relative",
    paddingLeft: "var(--space-4)",
    "&::before": {
      content: '"-"',
      position: "absolute",
      left: 0,
    },
    "&:not(:last-child)": {
      marginBottom: "var(--space-1)",
    },
  }),
};
